import { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useLocation } from 'react-router-dom';

// Lista ampliata di font sans-serif moderni
const fonts = [
  {
    name: 'Inter',
    weights: [100, 200, 300, 400, 500, 600, 700, 800, 900]
  },
  {
    name: 'Plus Jakarta Sans',
    weights: [200, 300, 400, 500, 600, 700, 800]
  },
  {
    name: 'Outfit',
    weights: [100, 200, 300, 400, 500, 600, 700, 800, 900]
  },
  {
    name: 'DM Sans',
    weights: [100, 200, 300, 400, 500, 600, 700, 800, 900]
  },
  {
    name: 'Satoshi',
    weights: [300, 400, 500, 600, 700, 900]
  },
  {
    name: 'General Sans',
    weights: [200, 300, 400, 500, 600, 700]
  },
  {
    name: 'Figtree',
    weights: [300, 400, 500, 600, 700, 800, 900]
  },
  {
    name: 'Cabinet Grotesk',
    weights: [100, 200, 300, 400, 500, 600, 700, 800, 900]
  },
  {
    name: 'Urbanist',
    weights: [100, 200, 300, 400, 500, 600, 700, 800, 900]
  },
  {
    name: 'Sora',
    weights: [100, 200, 300, 400, 500, 600, 700, 800]
  },
  {
    name: 'Space Grotesk',
    weights: [300, 400, 500, 600, 700]
  },
  {
    name: 'Work Sans',
    weights: [100, 200, 300, 400, 500, 600, 700, 800, 900]
  },
  {
    name: 'Manrope',
    weights: [200, 300, 400, 500, 600, 700, 800]
  },
  {
    name: 'Lexend',
    weights: [100, 200, 300, 400, 500, 600, 700, 800, 900]
  },
  {
    name: 'Albert Sans',
    weights: [100, 200, 300, 400, 500, 600, 700, 800, 900]
  },
  {
    name: 'Instrument Sans',
    weights: [400, 500, 600, 700]
  },
  {
    name: 'Public Sans',
    weights: [100, 200, 300, 400, 500, 600, 700, 800, 900]
  },
  {
    name: 'Onest',
    weights: [100, 200, 300, 400, 500, 600, 700, 800, 900]
  },  {
    name: 'Roboto Flex',
    weights: [100, 200, 300, 400, 500, 600, 700, 800, 900],
    category: 'Variable'
  },
  {
    name: 'Archivo',
    weights: [100, 200, 300, 400, 500, 600, 700, 800, 900],
    category: 'Sans Serif'
  },
  {
    name: 'Bricolage Grotesque',
    weights: [200, 300, 400, 500, 600, 700, 800],
    category: 'Modern'
  },
  {
    name: 'Familjen Grotesk',
    weights: [400, 500, 600, 700],
    category: 'Geometric'
  },
  {
    name: 'Chivo',
    weights: [100, 200, 300, 400, 500, 600, 700, 800, 900],
    category: 'Sans Serif'
  }
];

export default function FontPicker() {
  const { user } = useAuth();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedFont, setSelectedFont] = useState('');
  const [selectedWeight, setSelectedWeight] = useState(400);
  const [searchQuery, setSearchQuery] = useState('');
  const [fontLoaded, setFontLoaded] = useState(false);

  useEffect(() => {
    if (selectedFont) {
      const font = fonts.find(f => f.name === selectedFont);
      if (font) {
        const link = document.createElement('link');
        const weights = font.weights.join(';');
        link.href = `https://fonts.googleapis.com/css2?family=${selectedFont.replace(' ', '+')}:wght@${weights}&display=swap`;
        link.rel = 'stylesheet';
        document.head.appendChild(link);

        document.documentElement.style.setProperty('--font-family', `'${selectedFont}', sans-serif`);
        document.documentElement.style.setProperty('--font-weight', selectedWeight.toString());

        document.fonts.ready.then(() => {
          setFontLoaded(true);
        });
      }
    }
  }, [selectedFont, selectedWeight]);

  // Controllo dopo gli hooks
  if (!user || 
      user.role !== 'admin' || 
      !location.pathname.startsWith('/admin')) {
    return null;
  }

  const filteredFonts = fonts.filter(font => 
    font.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className={`fixed right-0 top-20 z-[9999] transition-transform duration-300 ${isOpen ? 'translate-x-0' : 'translate-x-[calc(100%-2.5rem)]'}`}>
      {/* Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="absolute left-0 top-0 bg-pizzaveg text-white px-2 py-4 rounded-l-lg writing-mode-vertical transform rotate-180 shadow-lg hover:bg-[#5a8642] transition-colors"
        style={{ writingMode: 'vertical-rl' }}
      >
        Font Picker
      </button>

      {/* Font Panel */}
      <div className="bg-white shadow-xl rounded-l-lg w-80 flex flex-col h-[700px]">
        <div className="p-4 border-b">
          <h3 className="text-lg font-semibold mb-3">Font Sans-Serif</h3>
          
          {/* Search input */}
          <input
            type="text"
            placeholder="Cerca font..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-pizzaveg"
          />

          {/* Selected font info */}
          {selectedFont && (
            <div className="mt-3 p-3 bg-green-50 rounded-md">
              <div className="text-sm text-gray-600">Font attivo:</div>
              <div className="font-medium text-pizzaveg">{selectedFont}</div>
              <div className="mt-2">
                <label className="text-sm text-gray-600 block mb-1">Peso:</label>
                <select
                  value={selectedWeight}
                  onChange={(e) => setSelectedWeight(Number(e.target.value))}
                  className="w-full px-2 py-1 border rounded focus:outline-none focus:ring-2 focus:ring-pizzaveg"
                >
                  {fonts.find(f => f.name === selectedFont)?.weights.map(weight => (
                    <option key={weight} value={weight}>
                      {weight} - {getWeightName(weight)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>
        
        {/* Font list */}
        <div className="flex-1 overflow-y-auto custom-scrollbar">
          <div className="p-4 space-y-2">
            {filteredFonts.map(font => (
              <button
                key={font.name}
                onClick={() => {
                  setSelectedFont(font.name);
                  setSelectedWeight(400); // Reset to regular weight
                }}
                className={`w-full text-left p-3 rounded hover:bg-gray-100 transition-colors ${
                  selectedFont === font.name ? 'bg-green-50 text-pizzaveg ring-1 ring-pizzaveg' : ''
                }`}
                style={{ 
                  fontFamily: `'${font.name}', sans-serif`,
                  fontWeight: selectedFont === font.name ? selectedWeight : 400
                }}
              >
                <span className="text-lg block">{font.name}</span>
                <p className="text-sm opacity-75 mt-1">
                  AaBbCcDd 123456
                </p>
                <div className="text-xs text-gray-500 mt-1">
                  {font.weights.length} pesi disponibili
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// Utility function per i nomi dei pesi
function getWeightName(weight: number): string {
  const weightNames: Record<number, string> = {
    100: 'Thin',
    200: 'Extra Light',
    300: 'Light',
    400: 'Regular',
    500: 'Medium',
    600: 'Semi Bold',
    700: 'Bold',
    800: 'Extra Bold',
    900: 'Black'
  };
  return weightNames[weight] || '';
} 